import { z } from 'zod';
import { supabase } from '@/lib/supabase/client';
import type { Tables, TablesUpdate } from '@/lib/supabase/types';

export type Profile = Tables<'profiles'>;

const HANDLE_RE = /^[a-z0-9][a-z0-9_-]{2,29}$/;

export const handleSchema = z
  .string()
  .min(3, 'Handle must be at least 3 characters')
  .max(30, 'Handle must be 30 characters or fewer')
  .regex(HANDLE_RE, 'Handle must be lowercase letters, numbers, hyphens, or underscores, starting with a letter or number');

const updateProfileSchema = z.object({
  display_name: z.string().max(80, 'Display name must be 80 characters or fewer').nullable().optional(),
  bio: z.string().max(500, 'Bio must be 500 characters or fewer').nullable().optional(),
});

export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data;
}

export async function getPublicProfile(handle: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('handle', handle)
    .single();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data;
}

export async function updateProfile(updates: Pick<TablesUpdate<'profiles'>, 'display_name' | 'bio'>): Promise<Profile> {
  const parsed = updateProfileSchema.parse(updates);
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('profiles')
    .update(parsed)
    .eq('id', user.id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function claimHandle(handle: string): Promise<void> {
  handleSchema.parse(handle);
  const { error } = await supabase.rpc('claim_handle', { p_handle: handle });
  if (error) throw error;
}

export function stringToColor(str: string): string {
  const colors = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#be185d'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export function profileInitials(profile: Profile): string {
  if (profile.display_name) {
    return profile.display_name
      .split(' ')
      .slice(0, 2)
      .map(w => w[0])
      .join('')
      .toUpperCase();
  }
  return (profile.handle ?? '?')[0].toUpperCase();
}

export function profileDisplayName(profile: Profile): string {
  return profile.display_name ?? profile.handle ?? 'Unknown';
}
